import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, Select, TimePicker, message } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Breadcrumbs from '../../components/Breadcrumbs';
import LocationCascade from '../../components/LocationCascade';
import {
  DdDetailsIcon,
  DdAssigneeIcon,
  DdReferenceIcon,
  DdRecurrenceIcon,
  DdClockIcon,
  PaperclipIcon,
  TrashIcon,
  TemplateDocIcon,
  ImagePlaceholderIcon,
} from '../../assets/icons';
import { useGetUsersQuery, userName } from '../../features/users/usersApi';
import { useUploadS3Mutation } from '../../features/uploads/uploadApi';
import {
  useGetDirectionQuery,
  useCreateDirectionMutation,
  useUpdateDirectionMutation,
  dirTitle,
  dirOneLine,
  dirDescription,
  dirPriority,
  dirDueDate,
  dirDueTime,
  dirDuration,
  dirFrequency,
  dirStartDate,
  dirEndDate,
  dirIsRecurring,
  dirMonthlyPattern,
  dirWeekday,
  dirReferencePerson,
  dirReferencePersonName,
  useGetReferencePersonsQuery,
  referencePersonName,
  referencePersonKey,
  referencePersonPhones,
  type DailyDirection,
  type DirectionAsset,
} from '../../features/dailyDirections/dailyDirectionApi';
import {
  useGetMyTreeQuery,
  useGetOrganizationsQuery,
} from '../../features/organizations/organizationsApi';
import {
  useGetAssigneesQuery,
  useGetAttachmentsQuery,
  assigneeName,
  attachmentName,
  type Assignee,
  type Attachment,
} from '../../features/activity/activityApi';

// Needed for parsing time-only strings ("HH:mm:ss") back into the TimePicker.
dayjs.extend(customParseFormat);

const FIELD =
  'w-full rounded-lg border border-transparent bg-[#f9fafb] px-4 text-sm text-ink placeholder:text-ink-secondary transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

const PRIORITIES: { key: string; lvl: string; label: string; critical?: boolean }[] = [
  { key: 'low', lvl: 'Level 01', label: 'Low' },
  { key: 'medium', lvl: 'Level 02', label: 'Medium' },
  { key: 'high', lvl: 'Level 03', label: 'High' },
  { key: 'critical', lvl: 'Level 04', label: 'Critical', critical: true },
];

interface DDFormValues {
  title: string;
  oneLine: string;
  description: string;
  assignees: number[];
  priority: string;
  dueDate: string;
  scheduleEndDate: string;
  dueTime: string;
  duration: string;
  repeat: boolean;
  frequency: string;
  monthlyPattern: string;
  weekday: string;
  startDate: string;
  endDate: string;
  assets: DirectionAsset[];
  // Influence Person
  referenceScope: string; // 'inside' | 'outside' | 'delhi'
  refDesignation: string;
  refUserId: number | ''; // Within constituency → picked from the reference-person dropdown
  refUserSource: string; // 'directory' | 'user' — disambiguates colliding ids
  refUserName: string; // Outside / NCR → typed name
  refPhones: string[]; // one or more contact numbers
  refLocation: string; // Within constituency → picked from the location tree
}

/** Reference-person scope options. "inside" (within constituency) shows the person +
 * location dropdowns; the scope keys are the values the backend expects. */
const REFERENCE_SCOPES = [
  { key: 'inside', label: 'Within Constituency' },
  { key: 'outside', label: 'Outside Constituency' },
  { key: 'delhi', label: 'NCR' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'on_status_changed', label: 'On Status Changed' },
];
const MONTHLY_PATTERNS = [
  'Same day each month',
  'First Saturday',
  'First day of the month',
  'Last day of the month',
];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const validationSchema = Yup.object({
  title: Yup.string().trim().required('Strategic title is required'),
  oneLine: Yup.string().trim().required('One-line direction is required'),
  refPhones: Yup.array().of(
    Yup.string().test(
      'phone',
      'Enter a valid 10-digit mobile number',
      (v) => !v || /^\d{10}$/.test((v ?? '').replace(/\s+/g, '')),
    ),
  ),
});

function Card({
  Icon,
  iconBg = 'bg-brand-shade text-brand',
  title,
  action,
  children,
}: {
  Icon: ReactNode;
  iconBg?: string;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-6 rounded-xl border border-[#f3f4f6] bg-white p-6 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={['flex size-8 shrink-0 items-center justify-center rounded-lg', iconBg].join(' ')}>
            {Icon}
          </span>
          <h2 className="text-lg font-semibold text-ink">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Label({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <span className="text-xs font-medium text-ink">
      {children}
      {required && <span className="text-[#ef4444]"> *</span>}
    </span>
  );
}

/** The numeric user id of an assignee record (user may be an id or a nested object). */
const assigneeUserId = (a: Assignee): number | undefined =>
  typeof a.user === 'number' ? a.user : (a.user?.id ?? undefined);

/** Map an activity Attachment to the form's asset shape. */
const attachmentToAsset = (a: Attachment): DirectionAsset => ({
  id: a.id,
  file_url: a.file_url ?? undefined,
  file_type: a.file_type ?? undefined,
  name: attachmentName(a),
});

export default function CreateDailyDirectionPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);

  const { data: editDirection } = useGetDirectionQuery(editId!, { skip: !isEdit });
  // Edit mode: assignees & assets come from the activity endpoints (keyed by entity).
  const { data: editAssignees } = useGetAssigneesQuery(
    { entityType: 'DailyDirection', entityId: editId! },
    { skip: !isEdit },
  );
  const { data: editAttachments } = useGetAttachmentsQuery(
    { entityType: 'DailyDirection', entityId: editId! },
    { skip: !isEdit },
  );
  const [createDirection, { isLoading: isCreating }] = useCreateDirectionMutation();
  const [updateDirection, { isLoading: isUpdating }] = useUpdateDirectionMutation();
  const [uploadS3] = useUploadS3Mutation();
  const [uploading, setUploading] = useState(false);
  const saving = isCreating || isUpdating;

  // Assignee dropdown — active users, server-side search.
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setUserSearch(userSearchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [userSearchInput]);
  const { data: usersData } = useGetUsersQuery({
    isActive: true,
    all: true,
    ...(userSearch ? { search: userSearch } : {}),
  });
  // Accumulate id → name so selected assignees keep their labels when a search narrows the list.
  const [nameMap, setNameMap] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!usersData?.items) return;
    setNameMap((prev) => {
      const next = { ...prev };
      usersData.items.forEach((u) => {
        next[u.id] = userName(u);
      });
      return next;
    });
  }, [usersData]);

  // Reference-person dropdown — org-scoped, server-side search (within-constituency case).
  const { data: myTree } = useGetMyTreeQuery();
  // TEMP: default the user flow to the "Alwar BJP Test" org for now (falls back to
  // the signed-in user's own org). Resolved by name so it works across environments.
  const { data: orgsData } = useGetOrganizationsQuery({ all: true });
  const defaultOrg = orgsData?.items?.find(
    (o) => o.name?.trim().toLowerCase() === 'alwar bjp test',
  )?.id;
  const organization = defaultOrg ?? myTree?.organization ?? undefined;
  const [refPersonInput, setRefPersonInput] = useState('');
  const [refPersonSearch, setRefPersonSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setRefPersonSearch(refPersonInput.trim()), 300);
    return () => clearTimeout(t);
  }, [refPersonInput]);
  const { data: refPersons } = useGetReferencePersonsQuery(
    { organization: organization!, ...(refPersonSearch ? { search: refPersonSearch } : {}) },
    { skip: organization == null },
  );
  // `key` ("<source>-<id>") is the unique dropdown value — a bare id collides across the
  // directory/user sources. `source`/`refId` ride along so onChange can send them on save.
  const refPersonOptions = (refPersons ?? []).map((p) => ({
    value: referencePersonKey(p),
    label: referencePersonName(p),
    source: p.source ?? 'directory',
    refId: p.id,
    designation: p.designation ?? '',
    location: p.location ?? '',
    phones: referencePersonPhones(p),
  }));
  // The currently-selected person, pinned so its label always resolves even after the
  // async search reloads `refPersons` (otherwise antd shows a stale/wrong name).
  const [pinnedRef, setPinnedRef] = useState<{ value: string; label: string } | null>(null);

  const formik = useFormik<DDFormValues>({
    initialValues: {
      title: '',
      oneLine: '',
      description: '',
      assignees: [],
      priority: 'high',
      dueDate: '',
      scheduleEndDate: '',
      dueTime: '',
      duration: '',
      repeat: false,
      frequency: 'daily',
      monthlyPattern: 'Same day each month',
      weekday: 'Monday',
      startDate: '',
      endDate: '',
      assets: [],
      referenceScope: 'inside',
      refDesignation: '',
      refUserId: '' as number | '',
      refUserSource: '',
      refUserName: '',
      refPhones: [''],
      refLocation: '',
    },
    validationSchema,
    onSubmit: (values) => submit(values, undefined),
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue } = formik;
  const errorOf = (name: keyof DDFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  const submit = async (values: DDFormValues, status?: string) => {
    const payload = {
      title: values.title.trim(),
      oneLine: values.oneLine.trim(),
      description: values.description.trim(),
      priority: values.priority,
      // The Schedule "Start Date/Time" (server maps to direction_date / due_time).
      startingDate: values.dueDate || undefined,
      startingTime: values.dueTime || undefined,
      // The Schedule "End Date" (server maps to end_date). Defaults to start + 7 days.
      endingDate: values.scheduleEndDate || undefined,
      duration: values.duration || undefined,
      repeat: values.repeat,
      ...(values.repeat
        ? {
            frequency: values.frequency,
            // "Repeat Pattern" applies to monthly; "Repeats On" weekday applies to weekly.
            ...(values.frequency === 'monthly' ? { monthlyPattern: values.monthlyPattern } : {}),
            ...(values.frequency === 'weekly' ? { weekday: values.weekday } : {}),
            startDate: values.startDate || undefined,
            endDate: values.endDate || undefined,
          }
        : {}),
      assignees: values.assignees,
      assets: values.assets,
      referencePerson: {
        scope: values.referenceScope,
        designation: values.refDesignation.trim() || undefined,
        // One or more contact numbers — `phone` keeps the first for backward compatibility.
        ...(() => {
          const phones = values.refPhones.map((p) => p.trim()).filter(Boolean);
          return phones.length ? { phone: phones[0], phones } : {};
        })(),
        ...(values.referenceScope === 'inside'
          ? {
              // A picked person sends its id + source; a typed custom value sends a name.
              ...(values.refUserId !== ''
                ? { user: values.refUserId, source: values.refUserSource || undefined }
                : { name: values.refUserName.trim() || undefined }),
              location: values.refLocation || undefined,
            }
          : { name: values.refUserName.trim() || undefined }),
      },
      ...(status ? { status } : {}),
    };
    try {
      if (isEdit && editId) {
        await updateDirection({ id: editId, patch: payload }).unwrap();
      } else {
        await createDirection(payload).unwrap();
      }
      navigate('/user/directions');
    } catch {
      message.error(`Could not ${isEdit ? 'update' : 'create'} the direction. Please try again.`);
    }
  };

  // Upload picked files to S3, then append the returned URLs to the assets list.
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: DirectionAsset[] = [];
      for (const file of Array.from(files)) {
        const res = await uploadS3(file).unwrap();
        uploaded.push({
          file_url: res.url,
          file_type: res.content_type || file.type,
          name: file.name,
        });
      }
      setFieldValue('assets', [...values.assets, ...uploaded]);
    } catch {
      message.error('File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  const removeAsset = (idx: number) =>
    setFieldValue('assets', values.assets.filter((_, i) => i !== idx));

  // Assignee options = fetched users, plus any selected id the current search dropped.
  const userOptions = useMemo(() => {
    const fetched = (usersData?.items ?? []).map((u) => ({ value: u.id, label: userName(u) }));
    const ids = new Set(fetched.map((o) => o.value));
    const extra = values.assignees
      .filter((id) => !ids.has(id))
      .map((id) => ({ value: id, label: nameMap[id] || `User ${id}` }));
    return [...fetched, ...extra];
  }, [usersData, values.assignees, nameMap]);

  // Repeatable single-select assignee rows (empty slots allowed) — synced to formik.assignees.
  const [assigneeRows, setAssigneeRows] = useState<(number | '')[]>(['']);
  const syncAssignees = (rows: (number | '')[]) => {
    setAssigneeRows(rows);
    setFieldValue(
      'assignees',
      Array.from(new Set(rows.filter((r): r is number => typeof r === 'number'))),
    );
  };
  const addAssigneeRow = () => setAssigneeRows((r) => [...r, '']);
  const setAssigneeRow = (i: number, v: number | '') =>
    syncAssignees(assigneeRows.map((r, idx) => (idx === i ? v : r)));
  const removeAssigneeRow = (i: number) => {
    const next = assigneeRows.filter((_, idx) => idx !== i);
    syncAssignees(next.length ? next : ['']);
  };

  // Edit mode: prefill from the loaded direction.
  useEffect(() => {
    if (!editDirection) return;
    const d = editDirection as DailyDirection;
    setFieldValue('title', dirTitle(d) === 'Untitled direction' ? '' : dirTitle(d));
    setFieldValue('oneLine', dirOneLine(d));
    setFieldValue('description', dirDescription(d));
    setFieldValue('priority', (dirPriority(d) || 'high').toLowerCase());
    setFieldValue('dueDate', dirDueDate(d));
    // Main schedule end date (get-one `end_date`); read-only in the form.
    if (d.end_date) setFieldValue('scheduleEndDate', d.end_date);
    setFieldValue('dueTime', dirDueTime(d));
    setFieldValue('duration', dirDuration(d));
    setFieldValue('repeat', dirIsRecurring(d));
    if (dirFrequency(d)) setFieldValue('frequency', dirFrequency(d));
    if (dirMonthlyPattern(d)) setFieldValue('monthlyPattern', dirMonthlyPattern(d));
    if (dirWeekday(d)) setFieldValue('weekday', dirWeekday(d));
    setFieldValue('startDate', dirStartDate(d));
    setFieldValue('endDate', dirEndDate(d));
    // Influence Person
    const rp = dirReferencePerson(d);
    if (rp) {
      const scope = rp.scope || 'inside';
      setFieldValue('referenceScope', scope);
      setFieldValue('refDesignation', rp.designation ?? '');
      const phones =
        Array.isArray(rp.phones) && rp.phones.length ? rp.phones : rp.phone ? [rp.phone] : [''];
      setFieldValue('refPhones', phones.length ? phones : ['']);
      const uid = typeof rp.user === 'number' ? rp.user : '';
      // `name` is the current key for a typed value; fall back to a legacy `user` string.
      const typedName = rp.name ?? (typeof rp.user === 'string' ? rp.user : '');
      if (scope === 'inside') {
        const source = rp.source ?? 'directory';
        setFieldValue('refUserId', uid);
        setFieldValue('refUserSource', typeof uid === 'number' ? source : '');
        setFieldValue('refUserName', uid === '' ? typedName : '');
        setFieldValue('refLocation', rp.location ?? '');
        // Pin the saved person's label so the Select shows the right name on load.
        if (typeof uid === 'number') {
          setPinnedRef({
            value: `${source}-${uid}`,
            label: dirReferencePersonName(d) || typedName || `Person ${uid}`,
          });
        }
      } else {
        setFieldValue('refUserName', typedName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDirection]);

  // Edit mode: prefill assignees from /activity/assignee/ (keep their names for the Select).
  useEffect(() => {
    if (!editAssignees) return;
    const ids = editAssignees
      .map(assigneeUserId)
      .filter((v): v is number => typeof v === 'number');
    setFieldValue('assignees', ids);
    setAssigneeRows(ids.length ? ids : ['']);
    setNameMap((prev) => {
      const next = { ...prev };
      editAssignees.forEach((a) => {
        const uid = assigneeUserId(a);
        if (uid != null) next[uid] = assigneeName(a);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAssignees]);

  // Edit mode: prefill assets from /activity/attachment/.
  useEffect(() => {
    if (!editAttachments) return;
    setFieldValue('assets', editAttachments.map(attachmentToAsset));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAttachments]);

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <Breadcrumbs
          items={[
            { label: 'Daily Directions', to: '/user/directions' },
            { label: isEdit ? 'Edit Daily Direction' : 'Create Daily Direction' },
          ]}
        />
        <h1 className="text-2xl font-semibold text-ink">
          {isEdit ? 'Edit Daily Direction' : 'Create Daily Direction'}
        </h1>
        <p className="whitespace-nowrap text-sm text-ink-secondary">
          Initialize a new operational directive. Define the scope, priority, and temporal
          constraints for tactical execution.
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Direction Details */}
          <Card Icon={<DdDetailsIcon className="size-4" />} title="Direction Details">
            <div className="flex flex-col gap-5">
              <label className="flex flex-col gap-2">
                <Label required>Strategic Title</Label>
                <input
                  name="title"
                  placeholder="e.g., Q4 Logistics Re-alignment"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={FIELD}
                />
                {errorOf('title') && (
                  <span className="text-xs font-medium text-[#ef4444]">{errorOf('title')}</span>
                )}
              </label>
              <label className="flex flex-col gap-2">
                <Label required>One-line Direction</Label>
                <input
                  name="oneLine"
                  placeholder="Summarize the core objective in under 100 characters"
                  value={values.oneLine}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={FIELD}
                />
                {errorOf('oneLine') && (
                  <span className="text-xs font-medium text-[#ef4444]">{errorOf('oneLine')}</span>
                )}
              </label>
              <label className="flex flex-col gap-2">
                <Label>Full Operational Description</Label>
                <textarea
                  name="description"
                  rows={4}
                  placeholder="Provide detailed context, secondary objectives, and mission-critical notes..."
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={[FIELD, 'resize-none py-3'].join(' ')}
                />
              </label>
            </div>
          </Card>

          {/* Influence Person */}
          <Card Icon={<DdReferenceIcon className="size-5" />} title="Influence Person">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="flex flex-wrap items-center gap-6 sm:col-span-2">
                {REFERENCE_SCOPES.map((o) => (
                  <label key={o.key} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                    <input
                      type="radio"
                      name="referenceScope"
                      checked={values.referenceScope === o.key}
                      onChange={() => setFieldValue('referenceScope', o.key)}
                      className="size-4 accent-brand"
                    />
                    {o.label}
                  </label>
                ))}
              </div>

              {/* Select Influence Person — dropdown within the constituency, free-text otherwise */}
              {values.referenceScope === 'inside' ? (
                <label className="order-1 flex flex-col gap-2">
                  <Label>Select Influence Person</Label>
                  <Select
                    className="field-muted w-full"
                    placeholder="Select or type an influence person"
                    showSearch
                    allowClear
                    filterOption={false}
                    onSearch={setRefPersonInput}
                    value={
                      // Prefer the exact selected key (the option's value may be a server
                      // `key` that differs from `${source}-${id}`; a mismatch would blank
                      // the input). Fall back to the reconstructed key, then a custom name.
                      pinnedRef?.value ??
                      (values.refUserId !== ''
                        ? `${values.refUserSource || 'directory'}-${values.refUserId}`
                        : values.refUserName || undefined)
                    }
                    onChange={(v, option) => {
                      const opt = Array.isArray(option) ? undefined : option;
                      const refId =
                        opt && typeof opt.refId === 'number' ? opt.refId : undefined;
                      if (refId !== undefined) {
                        // A directory/user person — store id + source, send both on save.
                        setFieldValue('refUserId', refId);
                        setFieldValue('refUserSource', (opt?.source as string) ?? 'directory');
                        setFieldValue('refUserName', '');
                        // Auto-fill designation + location + phones from the directory person.
                        setFieldValue('refDesignation', (opt?.designation as string) ?? '');
                        setFieldValue('refLocation', (opt?.location as string) ?? '');
                        {
                          const ph = (opt?.phones as string[] | undefined) ?? [];
                          setFieldValue('refPhones', ph.length ? ph : ['']);
                        }
                        setPinnedRef({ value: String(v), label: (opt?.label as string) ?? '' });
                      } else {
                        // Cleared or a typed custom value — leave the fields for the user.
                        setFieldValue('refUserId', '');
                        setFieldValue('refUserSource', '');
                        setFieldValue('refUserName', typeof v === 'string' ? v : '');
                        setFieldValue('refDesignation', '');
                        setFieldValue('refLocation', '');
                        setFieldValue('refPhones', ['']);
                        setPinnedRef(null);
                      }
                    }}
                    options={(() => {
                      const opts: {
                        value: string;
                        label: string;
                        source?: string;
                        refId?: number;
                        designation?: string;
                        location?: string;
                        phones?: string[];
                      }[] = [...refPersonOptions];
                      // Pin the selected person so its label always resolves, even when the
                      // async search has replaced the current results.
                      if (pinnedRef && !opts.some((o) => o.value === pinnedRef.value)) {
                        opts.unshift(pinnedRef);
                      }
                      const q = refPersonInput.trim();
                      // Offer the typed text as a custom option when it isn't an existing person.
                      if (q && !opts.some((o) => String(o.label).toLowerCase() === q.toLowerCase())) {
                        opts.push({ value: q, label: `Use “${q}”` });
                      }
                      // Keep a previously-typed custom name selectable/visible.
                      if (values.refUserName && !opts.some((o) => o.value === values.refUserName)) {
                        opts.unshift({ value: values.refUserName, label: values.refUserName });
                      }
                      return opts;
                    })()}
                    notFoundContent={organization == null ? 'No organization' : null}
                  />
                </label>
              ) : (
                <label className="order-1 flex flex-col gap-2">
                  <Label>Enter Influence Person</Label>
                  <input
                    name="refUserName"
                    placeholder="Influence person name"
                    value={values.refUserName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={FIELD}
                  />
                </label>
              )}

              <label className="order-2 flex flex-col gap-2">
                <Label>Enter Designation</Label>
                <input
                  name="refDesignation"
                  placeholder="ABC"
                  value={values.refDesignation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={FIELD}
                />
              </label>

              <div className="order-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label>Contact Number</Label>
                  <button
                    type="button"
                    onClick={() => setFieldValue('refPhones', [...values.refPhones, ''])}
                    className="text-xs font-semibold text-brand hover:underline"
                  >
                    + Add Another Number
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  {values.refPhones.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        inputMode="numeric"
                        placeholder="+91"
                        value={p}
                        onChange={(e) =>
                          setFieldValue(
                            'refPhones',
                            values.refPhones.map((x, idx) => (idx === i ? e.target.value : x)),
                          )
                        }
                        className={FIELD}
                      />
                      {values.refPhones.length > 1 && (
                        <button
                          type="button"
                          aria-label="Remove number"
                          onClick={() => {
                            const next = values.refPhones.filter((_, idx) => idx !== i);
                            setFieldValue('refPhones', next.length ? next : ['']);
                          }}
                          className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-[#e5e7eb] text-[#ef4444] transition-colors hover:bg-red-50"
                        >
                          <TrashIcon className="size-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {values.refPhones.some(
                  (p) => p.trim() && !/^\d{10}$/.test(p.replace(/\s+/g, '')),
                ) && (
                  <span className="text-xs font-medium text-[#ef4444]">
                    Enter valid 10-digit mobile numbers
                  </span>
                )}
              </div>

              {values.referenceScope === 'inside' && (
                <label className="order-3 flex flex-col gap-2">
                  <Label>Select Location</Label>
                  <LocationCascade
                    organization={organization}
                    value={values.refLocation}
                    placeholder="Location"
                    onChange={(sel) => setFieldValue('refLocation', sel ? String(sel.value) : '')}
                  />
                </label>
              )}
            </div>
          </Card>

          {/* Assignee */}
          <Card
            Icon={<DdAssigneeIcon className="h-3.5 w-[18px]" />}
            title="Assignee"
            action={
              <button
                type="button"
                onClick={addAssigneeRow}
                className="text-sm font-semibold text-brand transition-colors hover:text-brand-dark"
              >
                + Add More Assignee
              </button>
            }
          >
            <div className="flex flex-col gap-3">
              <Label>Select User</Label>
              {assigneeRows.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <Select
                      className="field-muted w-full"
                      placeholder="Choose an assignee..."
                      showSearch
                      filterOption={false}
                      onSearch={setUserSearchInput}
                      value={row === '' ? undefined : row}
                      onChange={(v) => setAssigneeRow(i, (v as number | undefined) ?? '')}
                      // Hide users already chosen in other rows so the same person
                      // can't be added twice (keep this row's own value visible).
                      options={userOptions.filter(
                        (o) =>
                          o.value === row ||
                          !assigneeRows.some((r, idx) => idx !== i && r === o.value),
                      )}
                    />
                  </div>
                  <button
                    type="button"
                    aria-label="Remove assignee"
                    onClick={() => removeAssigneeRow(i)}
                    className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-[#e5e7eb] text-[#ef4444] transition-colors hover:bg-red-50"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>

          {/* Recurrence */}
          <Card
            Icon={<DdRecurrenceIcon className="size-3.5" />}
            title="Recurrence"
            action={
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-ink">Repeat this Direction</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={values.repeat}
                  onClick={() => setFieldValue('repeat', !values.repeat)}
                  className={['relative h-5 w-11 shrink-0 rounded-full transition-colors', values.repeat ? 'bg-brand' : 'bg-[#d1d5db]'].join(' ')}
                >
                  <span
                    className={[
                      'absolute top-0 size-5 rounded-full border-4 bg-white transition-all',
                      values.repeat ? 'left-6 border-brand' : 'left-0 border-[#46556f]',
                    ].join(' ')}
                  />
                </button>
              </div>
            }
          >
            <div
              className={[
                'flex flex-col gap-4',
                values.repeat ? '' : 'pointer-events-none opacity-50',
              ].join(' ')}
            >
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex flex-1 flex-col gap-2">
                  <Label>Frequency</Label>
                  <Select
                    className="field-muted w-full"
                    value={values.frequency}
                    onChange={(v) => setFieldValue('frequency', v)}
                    options={FREQUENCIES}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <Label>Start Date</Label>
                  <DatePicker
                    className="field-muted w-full"
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                    value={values.startDate ? dayjs(values.startDate) : null}
                    onChange={(d) => setFieldValue('startDate', d ? d.format('YYYY-MM-DD') : '')}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <Label>End Date</Label>
                  <DatePicker
                    className="field-muted w-full"
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                    value={values.endDate ? dayjs(values.endDate) : null}
                    onChange={(d) => setFieldValue('endDate', d ? d.format('YYYY-MM-DD') : '')}
                  />
                </div>
              </div>

              {/* Frequency-dependent options */}
              {values.frequency === 'monthly' && (
                <div className="flex flex-col gap-2 sm:max-w-[220px]">
                  <Label>Repeat Pattern</Label>
                  <Select
                    className="field-muted w-full"
                    value={values.monthlyPattern}
                    onChange={(v) => setFieldValue('monthlyPattern', v)}
                    options={MONTHLY_PATTERNS.map((p) => ({ value: p, label: p }))}
                  />
                </div>
              )}
              {values.frequency === 'weekly' && (
                <div className="flex flex-col gap-2 sm:max-w-[220px]">
                  <Label>Repeats On</Label>
                  <Select
                    className="field-muted w-full"
                    value={values.weekday}
                    onChange={(v) => setFieldValue('weekday', v)}
                    options={WEEKDAYS.map((d) => ({ value: d, label: d }))}
                  />
                </div>
              )}
              {values.frequency === 'on_status_changed' && (
                <p className="text-xs text-ink-secondary">
                  This direction re-triggers automatically whenever the linked status changes — no
                  fixed schedule needed.
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Priority */}
          <Card
            Icon={<span className="font-black text-ink">!</span>}
            iconBg="bg-[#f3f4f6] text-ink"
            title="Priority"
          >
            <div className="grid grid-cols-2 gap-3">
              {PRIORITIES.map((p) => {
                const selected = values.priority === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setFieldValue('priority', p.key)}
                    className={[
                      'flex flex-col gap-1 rounded-lg p-3 text-left transition-colors',
                      selected ? 'border-2 border-brand bg-brand-shade' : 'border border-transparent bg-[#f9fafb] hover:border-brand/30',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'text-[10px] font-semibold uppercase tracking-[0.25px]',
                        selected ? 'text-brand' : p.critical ? 'text-[#ef4444]' : 'text-ink-secondary',
                      ].join(' ')}
                    >
                      {p.lvl}
                    </span>
                    <span className="text-sm font-medium text-ink">{p.label}</span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Schedule */}
          <Card Icon={<DdClockIcon className="size-3.5" />} title="Schedule">
            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <label className="flex flex-1 flex-col gap-2">
                  <Label>Start Date</Label>
                  <DatePicker
                    className="field-muted w-full"
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                    value={values.dueDate ? dayjs(values.dueDate) : null}
                    onChange={(d) => {
                      const val = d ? d.format('YYYY-MM-DD') : '';
                      setFieldValue('dueDate', val);
                      // End Date is read-only — always kept at start + 7 days.
                      setFieldValue('scheduleEndDate', d ? d.add(7, 'day').format('YYYY-MM-DD') : '');
                    }}
                  />
                </label>
                <label className="flex flex-1 flex-col gap-2">
                  <Label>End Date</Label>
                  <DatePicker
                    className="field-muted w-full"
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                    disabled
                    value={values.scheduleEndDate ? dayjs(values.scheduleEndDate) : null}
                  />
                </label>
              </div>
              <div className="flex gap-4">
                <div className="flex flex-1 flex-col gap-2">
                  <Label>Start Time</Label>
                  <TimePicker
                    className="field-muted w-full"
                    use12Hours
                    format="hh:mm A"
                    placeholder="05:00 PM"
                    value={values.dueTime ? dayjs(values.dueTime, 'HH:mm:ss') : null}
                    onChange={(t) => setFieldValue('dueTime', t ? t.format('HH:mm:ss') : '')}
                  />
                </div>
                <label className="flex flex-1 flex-col gap-2">
                  <Label>Duration</Label>
                  <input
                    name="duration"
                    placeholder="e.g. 2 Hours"
                    value={values.duration}
                    onChange={handleChange}
                    className={FIELD}
                  />
                </label>
              </div>
            </div>
          </Card>

          {/* Assets */}
          <Card Icon={<PaperclipIcon className="h-3.5 w-3 text-brand" />} title="Assets">
            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <label className="flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#d1d5db] p-6 text-center transition-colors hover:border-brand/50">
                  <TemplateDocIcon className="h-6 w-[18px] text-ink-secondary" />
                  <span className="text-xs font-medium text-ink">Upload Documents</span>
                  <span className="text-[10px] text-ink-secondary">PDF, DOC up to 10MB</span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    multiple
                    className="hidden"
                    onChange={(e) => onFiles(e.target.files)}
                  />
                </label>
                <label className="flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#d1d5db] p-6 text-center transition-colors hover:border-brand/50">
                  <ImagePlaceholderIcon className="size-6 text-ink-secondary" />
                  <span className="text-xs font-medium text-ink">Upload Photos</span>
                  <span className="text-[10px] text-ink-secondary">JPG, PNG captures</span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    multiple
                    className="hidden"
                    onChange={(e) => onFiles(e.target.files)}
                  />
                </label>
              </div>

              {uploading && <span className="text-xs text-ink-secondary">Uploading…</span>}

              {values.assets.length > 0 && (
                <div className="flex flex-col gap-2">
                  {values.assets.map((a, i) => (
                    <div
                      key={`${a.file_url}-${i}`}
                      className="flex items-center gap-2 rounded-lg bg-[#f9fafb] px-3 py-2"
                    >
                      <PaperclipIcon className="h-3.5 w-3 shrink-0 text-ink-secondary" />
                      <a
                        href={a.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 truncate text-xs text-brand hover:underline"
                      >
                        {a.name || a.file_url}
                      </a>
                      <button
                        type="button"
                        aria-label="Remove file"
                        onClick={() => removeAsset(i)}
                        className="shrink-0 text-ink-secondary transition-colors hover:text-red-500"
                      >
                        <TrashIcon className="size-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-4 border-t border-[#e5e7eb] pt-6">
        <button
          type="button"
          onClick={() => submit(values, 'Draft')}
          disabled={saving}
          className="rounded-lg px-6 py-2.5 text-sm font-medium text-ink-secondary transition-colors hover:bg-brand-shade disabled:opacity-60"
        >
          Save Draft
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-brand px-6 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark disabled:opacity-60"
        >
          {saving ? 'Saving…' : isEdit ? 'Update' : 'Submit'}
        </button>
      </div>
    </form>
  );
}
